import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Copy,
  Download,
  ExternalLink,
  ClipboardCheck,
  FileText,
  Clock,
  CheckCircle,
  Wallet,
  IndianRupee,
  Building2,
  Link as LinkIcon,
  FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-blue-100 text-blue-800', icon: Wallet },
};

const ApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasTabAccess } = useAuth();
  const canApprove = hasTabAccess('approvals') || user?.is_super_admin;
  
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState([]);
  // Local cache of per-vendor remittance meta (UTR + payment date). Synced
  // from approval.vendor_payment_meta on load; saved to backend on blur.
  const [vendorMeta, setVendorMeta] = useState({});
  // Email template intro/outro pulled from /settings/company so the user
  // (super-admin) can customise the boilerplate that wraps the HTML table.
  const [companySettings, setCompanySettings] = useState({});

  // Per-row edited amount-to-pay (mirrors the invoice item shape). Falls back
  // to whatever was loaded from server. Kept separate so we never mutate the
  // approval object directly until "Save Changes" is clicked.
  const [invoiceAmounts, setInvoiceAmounts] = useState({});
  
  // Form state for editable fields
  const [approvalLinks, setApprovalLinks] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [invoiceRemarks, setInvoiceRemarks] = useState({});

  const fetchApproval = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/approvals/${id}`, { withCredentials: true });
      setApproval(response.data);
      setVendorMeta(response.data.vendor_payment_meta || {});
      // Handle both old single link and new array format
      const links = response.data.approval_links || (response.data.approval_link ? [response.data.approval_link] : []);
      setApprovalLinks(links);
      
      // Initialize remarks
      const remarks = {};
      const amounts = {};
      response.data.invoices?.forEach(inv => {
        remarks[inv.invoice_id] = inv.remark || 'Amount due and invoiced';
        amounts[inv.invoice_id] = inv.amount ?? 0;
      });
      setInvoiceRemarks(remarks);
      setInvoiceAmounts(amounts);
    } catch (error) {
      toast.error('Failed to load approval');
      navigate('/approvals');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // One-shot load of company settings for the email-template wrapper.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
        setCompanySettings(res.data || {});
      } catch {
        /* silent — defaults are fine */
      }
    })();
  }, []);

  const fetchOpenInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/open?fiscal_year=${approval?.fiscal_year || ''}`, {
        withCredentials: true,
      });
      setOpenInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load open invoices');
    }
  };

  const fetchPaymentSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/approvals/${id}/payment-summary`, {
        withCredentials: true,
      });
      setPaymentSummary(response.data);
    } catch (error) {
      console.error('Failed to load payment summary');
    }
  }, [id]);

  useEffect(() => {
    fetchApproval();
  }, [fetchApproval]);

  useEffect(() => {
    if (approval) {
      fetchPaymentSummary();
    }
  }, [approval, fetchPaymentSummary]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update invoices with remarks AND the edited "amount to pay". The
      // backend caps every line at its source invoice's pending balance
      // and returns a 400 with the offending lines if any exceed the cap.
      const updatedInvoices = approval.invoices?.map(inv => ({
        ...inv,
        amount: invoiceAmounts[inv.invoice_id] ?? inv.amount,
        remark: invoiceRemarks[inv.invoice_id] || inv.remark
      })) || [];

      await axios.put(`${API}/approvals/${id}`, {
        approval_links: approvalLinks,
        invoices: updatedInvoices
      }, { withCredentials: true });
      
      toast.success('Approval saved successfully');
      fetchApproval();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save approval');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLink = () => {
    if (!newLink.trim()) {
      toast.error('Please enter a link');
      return;
    }
    // Basic URL validation
    if (!newLink.startsWith('http://') && !newLink.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }
    setApprovalLinks([...approvalLinks, newLink.trim()]);
    setNewLink('');
    toast.success('Link added');
  };

  const handleRemoveLink = (index) => {
    setApprovalLinks(approvalLinks.filter((_, i) => i !== index));
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/approvals/${id}`, {
        status: newStatus
      }, { withCredentials: true });
      
      toast.success(`Status updated to ${statusConfig[newStatus]?.label || newStatus}`);
      fetchApproval();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleOpenInvoiceDialog = () => {
    fetchOpenInvoices();
    // Pre-select already added invoices
    const existingIds = new Set(approval?.invoices?.map(inv => inv.invoice_id) || []);
    setSelectedInvoiceIds(existingIds);
    setInvoiceDialogOpen(true);
  };

  const handleInvoiceSelection = (invoiceId) => {
    // Hard guard: refuse to toggle if the underlying invoice's vendor is on hold.
    // Prevents any UI bypass (drag, programmatic, etc.) from staging a held vendor.
    const inv = openInvoices.find((i) => i.id === invoiceId);
    if (inv?.vendor_on_hold) {
      toast.error(`Vendor "${inv.vendor_name}" is on hold — invoice cannot be selected`);
      return;
    }
    const newSelection = new Set(selectedInvoiceIds);
    if (newSelection.has(invoiceId)) {
      newSelection.delete(invoiceId);
    } else {
      newSelection.add(invoiceId);
    }
    setSelectedInvoiceIds(newSelection);
  };

  const handleAddSelectedInvoices = async () => {
    const existingIds = new Set(approval?.invoices?.map(inv => inv.invoice_id) || []);
    const newInvoices = openInvoices.filter(inv => 
      selectedInvoiceIds.has(inv.id) && !existingIds.has(inv.id)
    );

    const invoiceItems = [
      ...(approval?.invoices || []),
      ...newInvoices.map(inv => ({
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        vendor_id: inv.vendor_id || '',
        vendor_name: inv.vendor_name || '',
        date: inv.date,
        // Use the live OUTSTANDING balance, not the gross net_payable.
        // For partially-paid invoices, sending net_payable would exceed
        // the pending amount and the backend correctly rejects it
        // ("Payment amount exceeds pending"). unpaid_amount is the
        // server's authoritative pending value (net_payable - paid).
        amount: inv.unpaid_amount ?? inv.net_payable ?? 0,
        budget_head: inv.budget_head || '',
        budget_consumption_pct: inv.budget_consumption_pct || 0,
        remark: 'Amount due and invoiced'
      }))
    ];

    try {
      await axios.put(`${API}/approvals/${id}`, {
        invoices: invoiceItems
      }, { withCredentials: true });
      
      toast.success(`Added ${newInvoices.length} invoice(s)`);
      setInvoiceDialogOpen(false);
      fetchApproval();
      fetchPaymentSummary();
    } catch (error) {
      // Surface the backend's specific reason (e.g. budget validation,
      // pending-balance overflow) instead of an opaque "Failed".
      const msg = error?.response?.data?.detail || 'Failed to add invoices';
      toast.error(msg);
    }
  };

  const handleRemoveInvoice = async (invoiceId) => {
    const updatedInvoices = approval.invoices.filter(inv => inv.invoice_id !== invoiceId);
    
    try {
      await axios.put(`${API}/approvals/${id}`, {
        invoices: updatedInvoices
      }, { withCredentials: true });
      
      toast.success('Invoice removed');
      fetchApproval();
      fetchPaymentSummary();
    } catch (error) {
      toast.error('Failed to remove invoice');
    }
  };

  const handleCopyTable = () => {
    if (!approval?.invoices?.length) {
      toast.error('No invoices to copy');
      return;
    }

    // Plain markdown table fallback (kept for backwards-compat).
    let tableText = '| Invoice No | Vendor | Date | Amount | Budget Head | Budget % | PO % | Remark |\n';
    tableText += '|------------|--------|------|--------|-------------|----------|------|--------|\n';
    
    approval.invoices.forEach(inv => {
      const raw = invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0;
      const pending = Math.max(0, (inv.invoice_net_payable || 0) - (inv.invoice_paid_amount || 0));
      const amt = Math.min(raw, pending);
      tableText += `| ${inv.invoice_number} | ${inv.vendor_name} | ${new Date(inv.date).toLocaleDateString('en-IN')} | ₹${amt.toLocaleString('en-IN')} | ${inv.budget_head || '-'} | ${inv.budget_consumption_pct || 0}% | ${inv.po_consumption_pct || 0}% | ${invoiceRemarks[inv.invoice_id] || inv.remark} |\n`;
    });
    
    tableText += `\n**Total Amount: ₹${(approval.total_amount || 0).toLocaleString('en-IN')}**`;

    navigator.clipboard.writeText(tableText).then(() => {
      toast.success('Table copied to clipboard (Markdown format)');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Copies a styled HTML version of the approval table to the clipboard, with
  // intro/outro boilerplate pulled from Company Settings. When pasted into
  // Gmail/Outlook compose the formatting (table, borders, colored progress
  // bars) survives because we set both `text/html` and a plaintext fallback.
  const handleCopyHtml = async () => {
    if (!approval?.invoices?.length) {
      toast.error('No invoices to copy');
      return;
    }

    const intro = (companySettings.approval_email_intro
      || 'Dear Sir,\n\nPlease check and approve the following invoices for payment:'
    ).replace(/\n/g, '<br>');
    const outro = (companySettings.approval_email_outro
      || 'Kindly revert with your approval at the earliest.\n\nThank you,'
    ).replace(/\n/g, '<br>');

    const rows = approval.invoices.map((inv) => {
      // Same hard cap that the on-screen input enforces — never put an
      // overpaid amount into the email table.
      const rawAmt = invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0;
      const pending = Math.max(
        0,
        (inv.invoice_net_payable || 0) - (inv.invoice_paid_amount || 0)
      );
      const isOverpay = rawAmt - pending > 1;
      const amt = isOverpay ? pending : rawAmt;
      const payPct = pending > 0 ? Math.min(100, Math.round((amt / pending) * 100)) : 0;
      const budgetPct = Number(inv.budget_consumption_pct || 0);
      const budgetColor = budgetPct >= 100 ? '#dc2626' : budgetPct >= 80 ? '#d97706' : '#16a34a';
      // PO consumption = (sum of invoices against this PO) / PO taxable_value.
      // Independent of budget consumption — tells the approver how much of
      // the PO has already been invoiced.
      const poPct = Number(inv.po_consumption_pct || 0);
      const poColor = poPct >= 100 ? '#dc2626' : poPct >= 80 ? '#d97706' : '#16a34a';
      const payColor = isOverpay ? '#dc2626' : payPct >= 100 ? '#16a34a' : '#d97706';
      const remark = invoiceRemarks[inv.invoice_id] || inv.remark || '';
      const payLabel = isOverpay
        ? 'OVERPAY (capped)'
        : payPct >= 100
        ? 'Full Payment'
        : `Partial (${payPct}%)`;
      return `
        <tr>
          <td style="padding:8px;border:1px solid #d1d5db;font-family:monospace;color:#1e3a8a;">${inv.invoice_number}</td>
          <td style="padding:8px;border:1px solid #d1d5db;">${escapeHtml(inv.vendor_name)}</td>
          <td style="padding:8px;border:1px solid #d1d5db;font-size:12px;">${escapeHtml(inv.department || '-')}</td>
          <td style="padding:8px;border:1px solid #d1d5db;font-size:12px;">${escapeHtml(inv.budget_head || '-')}</td>
          <td style="padding:8px;border:1px solid #d1d5db;font-size:12px;">${new Date(inv.date).toLocaleDateString('en-IN')}</td>
          <td style="padding:8px;border:1px solid #d1d5db;text-align:right;font-family:monospace;">₹${amt.toLocaleString('en-IN')}</td>
          <td style="padding:8px;border:1px solid #d1d5db;min-width:140px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <div style="flex:1;background:#e5e7eb;height:8px;border-radius:4px;overflow:hidden;">
                <div style="background:${budgetColor};height:100%;width:${Math.min(100, budgetPct)}%;"></div>
              </div>
              <span style="font-size:11px;color:${budgetColor};font-weight:600;">${budgetPct.toFixed(1)}%</span>
            </div>
          </td>
          <td style="padding:8px;border:1px solid #d1d5db;min-width:140px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <div style="flex:1;background:#e5e7eb;height:8px;border-radius:4px;overflow:hidden;">
                <div style="background:${poColor};height:100%;width:${Math.min(100, poPct)}%;"></div>
              </div>
              <span style="font-size:11px;color:${poColor};font-weight:600;">${poPct.toFixed(1)}%</span>
            </div>
          </td>
          <td style="padding:8px;border:1px solid #d1d5db;min-width:140px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <div style="flex:1;background:#e5e7eb;height:8px;border-radius:4px;overflow:hidden;">
                <div style="background:${payColor};height:100%;width:${payPct}%;"></div>
              </div>
              <span style="font-size:11px;color:${payColor};font-weight:600;">${payLabel}</span>
            </div>
          </td>
          <td style="padding:8px;border:1px solid #d1d5db;font-size:12px;">${escapeHtml(remark)}</td>
        </tr>
      `;
    }).join('');

    // Total displayed in the email reflects the (capped) edited amounts, not
    // whatever was last saved. This matches the user's intent when they copy
    // mid-edit. We re-fold the cap here to keep totals consistent.
    const liveTotal = approval.invoices.reduce((sum, inv) => {
      const raw = invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0;
      const pend = Math.max(
        0,
        (inv.invoice_net_payable || 0) - (inv.invoice_paid_amount || 0)
      );
      return sum + Math.min(raw, pend);
    }, 0);

    const html = `
      <div style="font-family:Arial,Helvetica,sans-serif;color:#111827;font-size:14px;">
        <p>${intro}</p>
        <table style="border-collapse:collapse;width:100%;margin:16px 0;font-size:13px;">
          <thead>
            <tr style="background:#1e3a8a;color:#ffffff;">
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Invoice No</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Vendor</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Department</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Budget Head</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Date</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:right;">Amount to Pay</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Budget Consumed</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">PO Consumed</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Payment Type</th>
              <th style="padding:10px;border:1px solid #1e3a8a;text-align:left;">Remark</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:#f3f4f6;font-weight:bold;">
              <td colspan="5" style="padding:10px;border:1px solid #d1d5db;text-align:right;">Total Amount</td>
              <td style="padding:10px;border:1px solid #d1d5db;text-align:right;font-family:monospace;">₹${liveTotal.toLocaleString('en-IN')}</td>
              <td colspan="4" style="padding:10px;border:1px solid #d1d5db;"></td>
            </tr>
          </tfoot>
        </table>
        <p>${outro}</p>
      </div>
    `.trim();

    // Plaintext fallback for email clients that strip HTML.
    const plain = `${(companySettings.approval_email_intro || '').trim()}\n\n` +
      approval.invoices.map(inv => {
        const amt = invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0;
        return `- ${inv.invoice_number} | ${inv.vendor_name} | ${inv.department || '-'} | ${inv.budget_head || '-'} | ₹${amt.toLocaleString('en-IN')}`;
      }).join('\n') +
      `\n\nTotal: ₹${(approval.total_amount || 0).toLocaleString('en-IN')}\n\n${(companySettings.approval_email_outro || '').trim()}`;

    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
      } else {
        // Older browsers — fall back to plain text.
        await navigator.clipboard.writeText(plain);
      }
      toast.success('HTML table copied. Paste into your email compose window.');
    } catch (err) {
      console.error('HTML clipboard write failed', err);
      toast.error('Could not copy HTML — your browser may have blocked it. Try again.');
    }
  };

  // Tiny HTML escape so vendor names with `<`/`&` don't blow up the table.
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/approvals/${id}/export-csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_summary_${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const saveVendorMeta = async (vendorId, field, value) => {
    // Optimistic local update
    setVendorMeta((m) => ({
      ...m,
      [vendorId]: { ...(m[vendorId] || {}), [field]: value },
    }));
    try {
      await axios.put(
        `${API}/approvals/${id}/vendor-payment-meta`,
        { vendor_id: vendorId, [field]: value },
        { withCredentials: true }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  // Per-vendor PDF: itemised list of invoices that make up the lump-sum
  // bank transfer to that vendor (Invoice #, Department, Budget head,
  // Remark, Amount). Attached with the transfer so the vendor knows
  // which invoices the consolidated amount covers.
  const handleDownloadVendorPdf = async (vendor) => {
    try {
      const response = await axios.get(
        `${API}/approvals/${id}/vendor-payment-pdf`,
        {
          params: { vendor_id: vendor.vendor_id },
          withCredentials: true,
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (vendor.vendor_name || 'vendor').replace(/[^a-z0-9]+/gi, '_');
      link.setAttribute('download', `payment_breakdown_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Breakdown PDF downloaded for ${vendor.vendor_name}`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to download breakdown PDF';
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Approval not found</p>
        <Button onClick={() => navigate('/approvals')} className="mt-4">
          Back to Approvals
        </Button>
      </div>
    );
  }

  const statusInfo = statusConfig[approval.status] || statusConfig.draft;
  const StatusIcon = statusInfo.icon;

  return (
    <div data-testid="approval-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/approvals')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">{approval.name}</h1>
            <p className="text-muted-foreground mt-1">FY {approval.fiscal_year}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${statusInfo.color} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {statusInfo.label}
          </Badge>
          {canApprove && (
            <>
              <Select value={approval.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Change Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Selected Invoices</p>
                <p className="text-3xl font-bold">{approval.invoices?.length || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ₹{(approval.total_amount || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Unique Vendors</p>
                <p className="text-3xl font-bold">{paymentSummary.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section A: Invoice Selection & Budget Lookup */}
      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading">Invoice Selection</CardTitle>
              <CardDescription>Select invoices for this payment approval</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={handleCopyTable} disabled={!approval.invoices?.length}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Table
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyHtml}
                disabled={!approval.invoices?.length}
                data-testid="copy-html-btn"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy as HTML (email)
              </Button>
              {canApprove && (
                <Button onClick={handleOpenInvoiceDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Invoices
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {!approval.invoices?.length ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">No invoices selected</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click &quot;Add Invoices&quot; to select open invoices
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="min-w-[120px]">Invoice No</TableHead>
                    <TableHead className="min-w-[140px]">Vendor</TableHead>
                    <TableHead className="min-w-[110px]">Department</TableHead>
                    <TableHead className="min-w-[110px]">Budget Head</TableHead>
                    <TableHead className="min-w-[90px]">Date</TableHead>
                    <TableHead className="text-right min-w-[140px]">Amount to Pay</TableHead>
                    <TableHead className="min-w-[160px]">Budget Consumed</TableHead>
                    <TableHead className="min-w-[160px]">PO Consumed</TableHead>
                    <TableHead className="min-w-[160px]">This Payment</TableHead>
                    <TableHead className="min-w-[180px]">Remark</TableHead>
                    {canApprove && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approval.invoices.map((inv) => {
                    const net = Number(inv.invoice_net_payable || 0);
                    const paid = Number(inv.invoice_paid_amount || 0);
                    const pending = Math.max(0, +(net - paid).toFixed(2));
                    const editedAmount = Number(
                      invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0
                    );
                    const over = editedAmount - pending > 1;
                    const within = !over && editedAmount > 0 && editedAmount <= pending;
                    const payPct =
                      pending > 0 ? Math.min(100, Math.round((editedAmount / pending) * 100)) : 0;
                    const isOverpay = over; // entered amount exceeds invoice pending
                    const budgetPct = Number(inv.budget_consumption_pct || 0);
                    const budgetTone =
                      budgetPct >= 100
                        ? 'bg-rose-500'
                        : budgetPct >= 80
                        ? 'bg-amber-500'
                        : 'bg-emerald-500';
                    const budgetText =
                      budgetPct >= 100
                        ? 'text-rose-700'
                        : budgetPct >= 80
                        ? 'text-amber-700'
                        : 'text-emerald-700';
                    // PO consumption — independent of budget. Shows how much
                    // of the parent PO has been invoiced (this invoice plus
                    // any others against the same PO).
                    const poPct = Number(inv.po_consumption_pct || 0);
                    const poTone =
                      poPct >= 100
                        ? 'bg-rose-500'
                        : poPct >= 80
                        ? 'bg-amber-500'
                        : 'bg-emerald-500';
                    const poText =
                      poPct >= 100
                        ? 'text-rose-700'
                        : poPct >= 80
                        ? 'text-amber-700'
                        : 'text-emerald-700';
                    const payTone = isOverpay
                      ? 'bg-rose-500'
                      : payPct >= 100
                      ? 'bg-emerald-500'
                      : 'bg-amber-500';
                    const payLabel = isOverpay
                      ? 'OVERPAY'
                      : payPct >= 100
                      ? 'Full'
                      : `${payPct}%`;
                    const payLabelTone = isOverpay
                      ? 'text-rose-700'
                      : payPct >= 100
                      ? 'text-emerald-700'
                      : 'text-amber-700';
                    return (
                      <TableRow
                        key={inv.invoice_id}
                        data-testid={`invoice-item-${inv.invoice_id}`}
                      >
                        <TableCell className="font-mono text-sm text-primary">
                          {inv.invoice_number}
                        </TableCell>
                        <TableCell
                          className="text-sm truncate max-w-[180px]"
                          title={inv.vendor_name}
                        >
                          {inv.vendor_name}
                        </TableCell>
                        <TableCell
                          className="text-sm truncate max-w-[140px]"
                          title={inv.department || '-'}
                        >
                          {inv.department || '-'}
                        </TableCell>
                        <TableCell
                          className="text-sm truncate max-w-[140px]"
                          title={inv.budget_head || '-'}
                        >
                          {inv.budget_head || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(inv.date).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              max={pending || undefined}
                              value={
                                invoiceAmounts[inv.invoice_id] ?? inv.amount ?? 0
                              }
                              disabled={!canApprove}
                              onChange={(e) => {
                                const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                setInvoiceAmounts((prev) => ({
                                  ...prev,
                                  [inv.invoice_id]: isNaN(v) ? 0 : v,
                                }));
                              }}
                              onBlur={(e) => {
                                // Hard guard: never let the field rest on a
                                // value that exceeds the invoice's pending
                                // balance. We revert silently and toast so
                                // the user sees what happened. Server already
                                // rejects but this prevents accidental copy
                                // of an overpaid amount into the HTML email.
                                const v = parseFloat(e.target.value) || 0;
                                if (v - pending > 1) {
                                  setInvoiceAmounts((prev) => ({
                                    ...prev,
                                    [inv.invoice_id]: pending,
                                  }));
                                  toast.warning(
                                    `${inv.invoice_number}: capped at ₹${pending.toLocaleString('en-IN')} (invoice pending)`
                                  );
                                }
                              }}
                              className={`h-8 w-32 text-right font-mono text-sm ${
                                over
                                  ? 'border-rose-500 ring-1 ring-rose-300 bg-rose-50'
                                  : within
                                  ? 'border-emerald-500 ring-1 ring-emerald-200 bg-emerald-50'
                                  : ''
                              }`}
                              data-testid={`pay-amount-${inv.invoice_id}`}
                              title={`Pending on this invoice: ₹${pending.toLocaleString('en-IN')}`}
                            />
                            <div className="text-[10px] text-muted-foreground">
                              of ₹{pending.toLocaleString('en-IN')} pending
                              {over && (
                                <span className="ml-1 text-rose-600 font-semibold">
                                  exceeds!
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden min-w-[60px]">
                              <div
                                className={`h-full ${budgetTone} transition-all`}
                                style={{ width: `${Math.min(100, budgetPct)}%` }}
                              />
                            </div>
                            <span
                              className={`text-[11px] font-semibold tabular-nums ${budgetText}`}
                            >
                              {budgetPct.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div
                            className="flex items-center gap-2"
                            data-testid={`po-consumed-${inv.invoice_id}`}
                            title={`PO consumed by all invoices: ${poPct.toFixed(1)}%`}
                          >
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden min-w-[60px]">
                              <div
                                className={`h-full ${poTone} transition-all`}
                                style={{ width: `${Math.min(100, poPct)}%` }}
                              />
                            </div>
                            <span
                              className={`text-[11px] font-semibold tabular-nums ${poText}`}
                            >
                              {poPct.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden min-w-[60px]">
                              <div
                                className={`h-full ${payTone} transition-all`}
                                style={{ width: `${payPct}%` }}
                              />
                            </div>
                            <span
                              className={`text-[11px] font-semibold tabular-nums ${payLabelTone}`}
                            >
                              {payLabel}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={invoiceRemarks[inv.invoice_id] || inv.remark || ''}
                            disabled={!canApprove}
                            onChange={(e) =>
                              setInvoiceRemarks((prev) => ({
                                ...prev,
                                [inv.invoice_id]: e.target.value,
                              }))
                            }
                            className="text-sm h-8 w-44"
                            placeholder="Add remark..."
                          />
                        </TableCell>
                        {canApprove && (
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveInvoice(inv.invoice_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section B: Approval Links (Multiple) */}
      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Approval Links
          </CardTitle>
          <CardDescription>Add links to approval documents, email threads, or related communications</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {/* Add new link */}
          <div className="flex gap-3">
            <Input
              placeholder="https://mail.google.com/... or any approval link"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
              className="flex-1"
              data-testid="new-approval-link-input"
            />
            <Button onClick={handleAddLink} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </Button>
          </div>
          
          {/* List of existing links */}
          {approvalLinks.length > 0 ? (
            <div className="space-y-2">
              {approvalLinks.map((link, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                  <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="flex-1 text-sm truncate text-primary hover:underline">
                    <a href={link} target="_blank" rel="noopener noreferrer" title={link}>
                      {link.length > 70 ? link.substring(0, 70) + '...' : link}
                    </a>
                  </span>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" asChild>
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                    {canApprove && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveLink(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No links added yet. Add email threads or approval documents above.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Section C: Vendor Payment Summary */}
      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading">Vendor Payment Summary</CardTitle>
              <CardDescription>Aggregated payment data by vendor for bank transfers</CardDescription>
            </div>
            <Button variant="outline" onClick={handleDownloadCSV} disabled={!paymentSummary.length}>
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {!paymentSummary.length ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">No vendors in this approval</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add invoices to see vendor payment summary
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Vendor Name</TableHead>
                    <TableHead>Bank Account Number</TableHead>
                    <TableHead>IFSC Code</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead className="text-center">Invoice Count</TableHead>
                    <TableHead className="min-w-[160px]">UTR No.</TableHead>
                    <TableHead className="min-w-[140px]">Payment Date</TableHead>
                    <TableHead className="text-center">Breakdown</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentSummary.map((vendor) => {
                    const meta = vendorMeta[vendor.vendor_id] || {};
                    return (
                    <TableRow key={vendor.vendor_id} data-testid={`vendor-summary-${vendor.vendor_id}`}>
                      <TableCell className="font-medium">{vendor.vendor_name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {vendor.bank_account_number || <span className="text-muted-foreground">Not provided</span>}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {vendor.ifsc_code || <span className="text-muted-foreground">Not provided</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-600">
                        ₹{vendor.total_amount.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{vendor.invoice_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={meta.utr_number || ''}
                          onChange={(e) => setVendorMeta((m) => ({
                            ...m,
                            [vendor.vendor_id]: { ...(m[vendor.vendor_id] || {}), utr_number: e.target.value },
                          }))}
                          onBlur={(e) => saveVendorMeta(vendor.vendor_id, 'utr_number', e.target.value)}
                          placeholder="UTR / Txn ID"
                          className="h-8 text-sm font-mono"
                          data-testid={`utr-input-${vendor.vendor_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={meta.payment_date || ''}
                          onChange={(e) => setVendorMeta((m) => ({
                            ...m,
                            [vendor.vendor_id]: { ...(m[vendor.vendor_id] || {}), payment_date: e.target.value },
                          }))}
                          onBlur={(e) => saveVendorMeta(vendor.vendor_id, 'payment_date', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`paydate-input-${vendor.vendor_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadVendorPdf(vendor)}
                          data-testid={`vendor-breakdown-pdf-${vendor.vendor_id}`}
                          title="Download invoice-level breakdown PDF to attach with this bank transfer"
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Selection Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Select Open Invoices</DialogTitle>
            <DialogDescription>
              Select invoices with unpaid balance to add to this approval
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {openInvoices.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No open invoices found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  All invoices are either paid or in draft status
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Invoice No</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Unpaid Amount</TableHead>
                      <TableHead>Budget Head</TableHead>
                      <TableHead className="text-right">Budget %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openInvoices.map((inv) => {
                      const blocked = !!inv.vendor_on_hold;
                      return (
                      <TableRow
                        key={inv.id}
                        className={blocked
                          ? 'opacity-60 bg-rose-50/40 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-muted/30'}
                        onClick={() => { if (!blocked) handleInvoiceSelection(inv.id); }}
                        data-testid={`open-invoice-row-${inv.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={!blocked && selectedInvoiceIds.has(inv.id)}
                            disabled={blocked}
                            onCheckedChange={() => { if (!blocked) handleInvoiceSelection(inv.id); }}
                            data-testid={`open-invoice-checkbox-${inv.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{inv.vendor_name}</span>
                            {blocked && (
                              <Badge
                                variant="outline"
                                className="h-5 px-1.5 py-0 text-[10px] gap-1 bg-rose-100 border-rose-300 text-rose-800"
                                title={inv.vendor_on_hold_reason || 'Vendor on hold — release the vendor on the Vendors page to add this invoice'}
                                data-testid={`open-invoice-hold-badge-${inv.id}`}
                              >
                                Vendor on hold
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(inv.date).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ₹{(inv.unpaid_amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-sm">{inv.budget_head || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={inv.budget_consumption_pct >= 80 ? 'destructive' : 'secondary'}>
                            {inv.budget_consumption_pct || 0}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSelectedInvoices} disabled={selectedInvoiceIds.size === 0}>
              Add Selected ({selectedInvoiceIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalDetail;
